/**
 * The message protocol between the **Captello mobile app** and an **application it
 * embeds** (the meeting platform, Connexions, …).
 *
 * This is the second of the SDK's two channels, and the roles are the reverse of the
 * capture-webview channel:
 *
 * | Channel                                   | Host                | Embedded            | Client on each side                                  |
 * | ----------------------------------------- | ------------------- | ------------------- | ---------------------------------------------------- |
 * | A host page embeds the capture webview    | your page           | capture webview     | `CaptelloWebview` (host side)                        |
 * | The mobile app embeds an application      | Captello mobile app | your application    | `EmbeddedAppClient` (app side), `MobileHostClient` (embedded side) |
 *
 * Wire format (match it exactly — it differs from the capture-webview channel):
 * - Messages are posted as **plain objects**, not JSON strings.
 * - `type` values are SCREAMING_CASE.
 * - {@link MobileHostRequestType} (embedded application → mobile app) and
 *   {@link MobileHostResponseType} (mobile app → embedded application) are disjoint.
 */

import type { Unsubscribe } from "./client";
import type { PrefillInfoItem } from "./messages";

/* ------------------------------------------------------------------ *
 * Requests: embedded application → mobile app
 * ------------------------------------------------------------------ */

/** Message `type` values an embedded application sends to the Captello mobile app. */
export enum MobileHostRequestType {
    /** The application finished loading; the app hides its loading spinner. */
    AppReady = "APP_READY",
    /** A user-facing error; the app shows `message`. */
    Error = "ERROR",
    /** Close the application (the app dismisses the modal or pops the route). */
    NavigateBack = "NAVIGATE_BACK",
    /** Ask for a magic token; answered with {@link MobileHostResponseType.AuthToken}. */
    RequestAuthToken = "REQUEST_AUTH_TOKEN",
    /**
     * Open the app's badge scanner. Answered with one
     * {@link MobileHostResponseType.ScannerResult} per scanned person, then
     * {@link MobileHostResponseType.ScannerClosed}.
     */
    OpenScanner = "OPEN_ULC_FORM_SCANNER",
    /** Open `url` in the system browser. */
    OpenUrl = "OPEN_URL",
    /** Copy `text` to the clipboard; acknowledged with `COPIED_SUCCESS` / `COPIED_ERROR`. */
    CopyText = "COPY_TEXT",
    /** Open the native share sheet. */
    ShareUrl = "SHARE_URL",
    /** Save a vCard to the contacts; acknowledged with `SAVE_VCARD_SUCCESS` / `SAVE_VCARD_ERROR`. */
    SaveVCard = "SAVE_VCARD",
    /** Add a wallet pass; acknowledged with `ADD_TO_WALLET_SUCCESS` / `ADD_TO_WALLET_ERROR`. */
    AddToWallet = "ADD_TO_WALLET",
    /** Ask the app to synchronize its data. */
    SyncApp = "SYNC_APP",
    /**
     * A meeting was booked in the application. The app takes the rep back to the screen
     * that opened the application, which shows the meeting from then on.
     */
    MeetingScheduled = "MEETING_SCHEDULED",
}

export interface AppReadyRequest {
    type: MobileHostRequestType.AppReady;
}
export interface ErrorRequest {
    type: MobileHostRequestType.Error;
    message: string;
}
export interface NavigateBackRequest {
    type: MobileHostRequestType.NavigateBack;
}
export interface RequestAuthTokenRequest {
    type: MobileHostRequestType.RequestAuthToken;
}
export interface OpenScannerRequest {
    type: MobileHostRequestType.OpenScanner;
}
export interface OpenUrlRequest {
    type: MobileHostRequestType.OpenUrl;
    url: string;
}
export interface CopyTextRequest {
    type: MobileHostRequestType.CopyText;
    text: string;
}
export interface ShareUrlRequest {
    type: MobileHostRequestType.ShareUrl;
    title: string;
    text: string;
}
export interface SaveVCardRequest {
    type: MobileHostRequestType.SaveVCard;
    /** vCard text. */
    text: string;
}
export interface AddToWalletRequest {
    type: MobileHostRequestType.AddToWallet;
    /** Base64 pass data, with or without a `data:` prefix. */
    text: string;
}
export interface SyncAppRequest {
    type: MobileHostRequestType.SyncApp;
}
export interface MeetingScheduledRequest {
    type: MobileHostRequestType.MeetingScheduled;
    /** The meeting platform's id of the booked meeting. */
    meetingId: number;
}

/** Discriminated union of every message an embedded application can send to the mobile app. */
export type MobileHostRequest =
    | AppReadyRequest
    | ErrorRequest
    | NavigateBackRequest
    | RequestAuthTokenRequest
    | OpenScannerRequest
    | OpenUrlRequest
    | CopyTextRequest
    | ShareUrlRequest
    | SaveVCardRequest
    | AddToWalletRequest
    | SyncAppRequest
    | MeetingScheduledRequest;

/** Maps each request `type` to its full message shape (used by {@link EmbeddedAppClient.onRequest}). */
export type MobileHostRequestMap = {
    [M in MobileHostRequest as M["type"]]: M;
};

/* ------------------------------------------------------------------ *
 * Responses: mobile app → embedded application
 * ------------------------------------------------------------------ */

/** Message `type` values the Captello mobile app sends to an embedded application. */
export enum MobileHostResponseType {
    /** Answer to {@link MobileHostRequestType.RequestAuthToken}. */
    AuthToken = "AUTH_TOKEN",
    /** One scanned person (or a scanner failure) after {@link MobileHostRequestType.OpenScanner}. */
    ScannerResult = "ULC_FORM_SCANNER_RESULT",
    /** The scanner session ended: every result was posted, or the user cancelled. */
    ScannerClosed = "ULC_FORM_SCANNER_CLOSED",
    CopiedSuccess = "COPIED_SUCCESS",
    CopiedError = "COPIED_ERROR",
    SaveVCardSuccess = "SAVE_VCARD_SUCCESS",
    SaveVCardError = "SAVE_VCARD_ERROR",
    AddToWalletSuccess = "ADD_TO_WALLET_SUCCESS",
    AddToWalletError = "ADD_TO_WALLET_ERROR",
}

/** Mobile app → embedded application: the magic token requested with {@link MobileHostRequestType.RequestAuthToken}. */
export interface AuthTokenMessage {
    type: MobileHostResponseType.AuthToken;
    token: string;
}

/**
 * Mobile app → embedded application: one scanned person, or a scanner failure.
 *
 * The app posts one of these per person the scanner captured — several for a group
 * scan — then a {@link ScannerClosedMessage}. `result` holds the looked-up attendee
 * fields in the same shape {@link PrefillInfoItem} uses, so they can be fed straight to
 * a capture form's `prefill({ info })`. It is empty (not absent) when the badge had no
 * lookup data; the badge can still be linked via `badgeId`.
 *
 * On failure `result` is absent and `message` carries the error text.
 */
export interface ScannerResultMessage {
    type: MobileHostResponseType.ScannerResult;
    /**
     * Badge ID the person was scanned from. Empty for business cards and manual search.
     * Absent altogether on app builds that predate multi-person sessions — those post a
     * single result and never a {@link ScannerClosedMessage}.
     */
    badgeId?: string;
    /** Looked-up attendee fields (success). */
    result?: PrefillInfoItem[];
    /** Error text (failure) — `result` is absent. */
    message?: string;
}

/** Mobile app → embedded application: the scanner session ended. Follows the last {@link ScannerResultMessage}. */
export interface ScannerClosedMessage {
    type: MobileHostResponseType.ScannerClosed;
}

interface AckMessage<T extends MobileHostResponseType> {
    type: T;
}

/** Discriminated union of every message the mobile app can send to an embedded application. */
export type MobileHostResponse =
    | AuthTokenMessage
    | ScannerResultMessage
    | ScannerClosedMessage
    | AckMessage<MobileHostResponseType.CopiedSuccess>
    | AckMessage<MobileHostResponseType.CopiedError>
    | AckMessage<MobileHostResponseType.SaveVCardSuccess>
    | AckMessage<MobileHostResponseType.SaveVCardError>
    | AckMessage<MobileHostResponseType.AddToWalletSuccess>
    | AckMessage<MobileHostResponseType.AddToWalletError>;

/** Maps each response `type` to its full message shape (used by {@link MobileHostClient.on}). */
export type MobileHostResponseMap = {
    [M in MobileHostResponse as M["type"]]: M;
};

/** One person captured by the mobile app's scanner. */
export interface ScannedPerson {
    /** Badge ID the person was scanned from; empty for business cards and manual search. */
    badgeId: string;
    /** Looked-up attendee fields; empty when the badge had no lookup data. */
    fields: PrefillInfoItem[];
}

/* ------------------------------------------------------------------ *
 * Runtime guards / parsing
 * ------------------------------------------------------------------ */

const REQUEST_TYPES: ReadonlySet<string> = new Set(Object.values(MobileHostRequestType));
const RESPONSE_TYPES: ReadonlySet<string> = new Set(Object.values(MobileHostResponseType));

function parseTyped(data: unknown, types: ReadonlySet<string>): Record<string, unknown> | null {
    let value: unknown = data;
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
    const record = value as Record<string, unknown>;
    if (typeof record["type"] !== "string" || !types.has(record["type"])) return null;
    return record;
}

/**
 * Parses a raw `MessageEvent.data` value into a typed {@link MobileHostRequest}, or
 * returns `null` if it is not a recognized request. Plain objects are the wire format;
 * a JSON string is accepted too for robustness.
 */
export function parseMobileHostRequest(data: unknown): MobileHostRequest | null {
    return parseTyped(data, REQUEST_TYPES) as unknown as MobileHostRequest | null;
}

/**
 * Parses a raw `MessageEvent.data` value into a typed {@link MobileHostResponse}, or
 * returns `null` if it is not a recognized response. Plain objects are the wire format;
 * a JSON string is accepted too for robustness.
 */
export function parseMobileHostResponse(data: unknown): MobileHostResponse | null {
    return parseTyped(data, RESPONSE_TYPES) as unknown as MobileHostResponse | null;
}

/** @internal Builds the callable-with-`unsubscribe()` handle every `on*` method returns. */
export function makeSubscription(off: () => void): Unsubscribe {
    const handle = (() => {
        off();
    }) as Unsubscribe;
    handle.unsubscribe = off;
    return handle;
}
